import { useState, useCallback, useMemo, useEffect } from 'react';
import { Page } from '@/api';

interface UseGridNavigationProps {
  totalItems: number;
  columnCount: number;
  initialSelection?: number;
  onSelectionChange?: (index: number) => void;
  pages?: Page[]; // Add pages array to check for labels
  onMarkBreach?: () => void; // Callback for '1' key
  onMarkBlank?: () => void; // Callback for '2' key
  onClearLabels?: () => void; // Callback for '3' key (mirrors the sidebar 'None' button)
  // Used to reset selectedIndex when the user navigates between documents.
  // We key on documentId rather than `pages` identity because Immer produces a
  // new `pages` reference on every label mutation — resetting on that would
  // bounce selection back to page 0 whenever a user marks breach/blank.
  documentId?: string;
}

export const useGridNavigation = ({
  totalItems,
  columnCount,
  initialSelection = 0,
  onSelectionChange,
  pages,
  onMarkBreach,
  onMarkBlank,
  onClearLabels,
  documentId,
}: UseGridNavigationProps) => {
  const [selectedIndex, setSelectedIndexState] = useState(initialSelection);

  useEffect(() => {
    setSelectedIndexState(0);
  }, [documentId]);

  const setSelectedIndex = useCallback(
    (newIndex: number) => {
      if (newIndex >= 0 && newIndex < totalItems) {
        setSelectedIndexState(newIndex);
        if (onSelectionChange) {
          onSelectionChange(newIndex);
        }
      }
    },
    [totalItems, onSelectionChange],
  );

  const isSelected = (index: number) => index === selectedIndex;

  // Helper function to check if a page has a label
  const hasLabel = useCallback((page: Page) => {
    return page.detectedBlank || page.detectedBreach || page.confirmedBlank || page.confirmedBreach;
  }, []);

  // Optimized: Build a Set of labeled page indexes for O(1) lookups
  // This auto-updates when pages array changes
  const labeledPageIndexes = useMemo(() => {
    if (!pages) return new Set<number>();

    const indexes = new Set<number>();
    pages.forEach((page, index) => {
      if (hasLabel(page)) {
        indexes.add(index);
      }
    });

    return indexes;
  }, [pages, hasLabel]);

  // Convert Set to sorted array for efficient navigation
  const sortedLabeledIndexes = useMemo(() => {
    return Array.from(labeledPageIndexes).sort((a, b) => a - b);
  }, [labeledPageIndexes]);

  // Optimized function to find next labeled page using pre-built index
  const findNextLabeledPage = useCallback(
    (startIndex: number) => {
      if (sortedLabeledIndexes.length === 0) return -1;

      // Find first index greater than startIndex
      const nextIndex = sortedLabeledIndexes.find((index) => index > startIndex);

      if (nextIndex !== undefined) {
        return nextIndex;
      }

      // Wrap around to first labeled page
      return sortedLabeledIndexes[0];
    },
    [sortedLabeledIndexes],
  );

  // Optimized function to find previous labeled page using pre-built index
  const findPreviousLabeledPage = useCallback(
    (startIndex: number) => {
      if (sortedLabeledIndexes.length === 0) return -1;

      // Find last index less than startIndex
      for (let i = sortedLabeledIndexes.length - 1; i >= 0; i--) {
        if (sortedLabeledIndexes[i] < startIndex) {
          return sortedLabeledIndexes[i];
        }
      }

      // Wrap around to last labeled page
      return sortedLabeledIndexes[sortedLabeledIndexes.length - 1];
    },
    [sortedLabeledIndexes],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const navKeys = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
        'l',
        'L',
        'k',
        'K',
        '9',
        '0',
        '1',
        '2',
        '3',
      ];
      if (!navKeys.includes(e.key)) {
        return; // Don't prevent default for other keys
      }

      let nextIndex = selectedIndex;
      switch (e.key) {
        case 'ArrowUp':
          nextIndex = selectedIndex - columnCount;
          break;
        case 'ArrowDown':
          nextIndex = selectedIndex + columnCount;
          break;
        case 'ArrowLeft':
          nextIndex = selectedIndex - 1;
          break;
        case 'ArrowRight':
          nextIndex = selectedIndex + 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = totalItems - 1;
          break;
        case 'l':
        case 'L':
        case '0':
          nextIndex = findNextLabeledPage(selectedIndex);
          if (nextIndex === -1) {
            return;
          }
          break;
        case 'k':
        case 'K':
        case '9':
          nextIndex = findPreviousLabeledPage(selectedIndex);
          if (nextIndex === -1) {
            return;
          }
          break;
        case '1':
          e.preventDefault();
          e.stopPropagation();
          if (onMarkBreach) {
            onMarkBreach();
          }
          return;
        case '2':
          e.preventDefault();
          e.stopPropagation();
          if (onMarkBlank) {
            onMarkBlank();
          }
          return;
        case '3':
          e.preventDefault();
          e.stopPropagation();
          if (onClearLabels) {
            onClearLabels();
          }
          return;
      }

      // Only prevent default if we are actually navigating within bounds
      if (nextIndex >= 0 && nextIndex < totalItems) {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(nextIndex);
      }
      // Otherwise, do nothing and let the browser handle the scroll
    },
    [
      selectedIndex,
      columnCount,
      totalItems,
      setSelectedIndex,
      findNextLabeledPage,
      findPreviousLabeledPage,
      onMarkBreach,
      onMarkBlank,
      onClearLabels,
    ],
  );

  return {
    selectedIndex,
    setSelectedIndex,
    isSelected,
    handleKeyDown,
    labeledPageIndexes,
    sortedLabeledIndexes,
    findNextLabeledPage,
    findPreviousLabeledPage,
  };
};
